import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLogout, useTranslate } from 'ra-core';
import { authProvider } from '@/authProvider.ts';
import { ForgotPasswordConfirm } from './components/ForgotPasswordConfirm';
import { useMutation } from '@tanstack/react-query';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Input } from '@/components/ui/input.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils.ts';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.tsx';

const ForgotPasswordPage = () => {
  const logout = useLogout();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [email, setEmail] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const t = useTranslate();
  const { mutate, isPending } = useMutation({
    mutationFn: authProvider.sendPasswordResetLink,
    onSuccess: () => {
      setFormSubmitted(true);
    },
    onError: () => {
      setFormSubmitted(true);
    },
  });

  useEffect(() => {
    void logout(undefined, false);
  }, [logout]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const recaptchaAction = 'forgot_password';

    const token = await executeRecaptcha?.(recaptchaAction);
    if (!token) {
      // Do nothing
      return;
    }

    mutate({ email, token, action: recaptchaAction });
  };

  if (formSubmitted) {
    return <ForgotPasswordConfirm email={email} />;
  }

  return (
    <>
      <Helmet>
        <title>{t('auth.message.reset_password.page_title')}</title>
      </Helmet>
      <div>
        <Link to="/">
          <img
            alt="logo"
            src="https://storage.googleapis.com/public-goloot-production/goloot/goloot-logo-white.png"
            style={{ height: 55, width: 'auto', margin: 'auto' }}
          />
        </Link>
      </div>

      <div className={cn('flex flex-col gap-6')}>
        <Card>
          <CardHeader className="">
            <CardTitle className="text-xl">{'Forgot Password'}</CardTitle>
            <CardDescription>
              {'Enter your email address to receive a password reset link.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleFormSubmit}>
              <div className="flex flex-col gap-3">
                <Input
                  autoComplete="username"
                  placeholder={t('auth.label.email')}
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
                <Button type="submit" disabled={isPending}>
                  {t('auth.button.send_reset_link')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default ForgotPasswordPage;
